import { CONFIGURATION_TYPE, ConfigurationModel } from '@3ddv/utils-igsu';
import { Injectable, Injector, assertInInjectionContext, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ResolveFn, Router } from '@angular/router';
import { EventModel, EventResponseModel } from 'projects/common/igsu-models';
import { BehaviorSubject, catchError, of } from 'rxjs';
import { ModalService } from '../../_shared/data-access/modal.service';
import { SectionsService } from '../../_shared/data-access/sections.service';
import { SessionService } from '../../_shared/data-access/session.service';
import { getMessageError } from '../../_shared/utils/get-message-error';
import { EventsAPI } from './_events.api';
import { EventsTicketsService } from './events-tickets.service';

/**
 * Service for managing events data. It provides functionality to fetch events related to a patron,
 * select an event, and navigate to the tickets page for the selected event.
 */
@Injectable({
  providedIn: 'root',
})
export class EventsService {
  private _api = inject(EventsAPI);
  private _router = inject(Router);
  private _injector = inject(Injector);
  private _modalService = inject(ModalService);
  private _sessionService = inject(SessionService);
  private _sectionService = inject(SectionsService);

  private readonly _events = new BehaviorSubject<EventModel[]>([]);
  public events$ = this._events.asObservable();

  private readonly _eventConfiguration = new BehaviorSubject<ConfigurationModel | null>(null);
  public readonly eventConfiguration$ = this._eventConfiguration.asObservable();

  private readonly routeToNavigate = '/events/tickets';
  public selectedEvent!: EventModel | undefined;
  public isEventError = false;

  // This a list for developers, project managers or club intermediaries to ignore the events expiry dates to facilitate testing
  private eventExpiryWhiteList = [
    'carlos@3ddigitalvenue.com',
    'paola@3ddigitalvenue.com',
    'victormartin@mobilemediacontent.com',
    'aconnor@sfgiants.com',
    'ncalder@redsox.com',
    'asoriano@mobilemediacontent.com',
    'epeksens@raysbaseball.com',
    'jreed@raysbaseball.com',
    'michelle.ahn@mlb.com',
  ];

  /**
   * Fetches patron events and tickets from the server and initializes the events stream.
   */
  public initialize = () => {
    this._sessionService.patron$.pipe(takeUntilDestroyed()).subscribe(patron => {
      if (!patron || this._events.getValue()?.length) {
        return;
      }
      return this._api.getInventory(patron.patronProvenueId).pipe(catchError(this._onError)).subscribe(this._onSuccess);
    });
  };

  /**
   * Sets an event as the selected event and navigates to the ticket page for it.
   * @param eventData - The event data to select.
   */
  public selectEventAndCheckForAvailabilityReset = (eventData: EventModel) => {
    if (this.selectedEvent && this.selectedEvent?.id !== eventData?.id) {
      this._sectionService.resetAvailableSectionData();
    }

    this.selectedEvent = eventData;
    this._router.navigate([this.routeToNavigate]);
  };

  public unselectEvent = () => {
    this.selectedEvent = undefined;
  };

  /**
   * Checks if there is any event currently selected.
   */
  public isSomeEventSelected = () => {
    return !!this.selectedEvent;
  };

  /**
   * Determines whether an event date has passed, marking it as expired.
   * @param  eventData - The date of the event to check.
   *
   * @note The whitelist is done in a very quick way, this could be done much better
   */
  public isExchangePastDue(eventData: EventModel) {
    const currentDate = new Date();

    const patronEmail = this._sessionService.getPatron()!.email;

    if (this.eventExpiryWhiteList.includes(patronEmail.toLowerCase())) {
      // If the email is on the white list, do always return that it is not expired
      return false;
    }

    // This is a workaround to some events not having a sale start date (comes as null from TDC)
    // but still we allow the exchange if the sale end date is correct.
    if (!eventData.exchangeSaleStartDate && eventData.exchangeSaleEndDate) {
      const exchangeEndDate = new Date(eventData.exchangeSaleEndDate);
      return currentDate > exchangeEndDate;
    }

    const exchangeStartDate = new Date(eventData.exchangeSaleStartDate);
    const exchangeEndDate = new Date(eventData.exchangeSaleEndDate);
    return exchangeStartDate > currentDate || currentDate > exchangeEndDate;
  }

  private _onSuccess = (eventsData: void | EventResponseModel) => {
    if (!eventsData || eventsData.events.length === 0) {
      return;
    }

    this._setEvents(eventsData.events);
    const eventTicketsService = this._injector.get(EventsTicketsService);
    eventTicketsService.setTicketData(eventsData.tickets);
    eventTicketsService.ticketsPerEvent = eventsData.ticketsPerEvent;
  };

  /**
   * Handles errors during payment creation by displaying a relevant error message to the user.
   * Depending on the error type, it suggests different actions to the user.
   *
   * @param err - The error object containing the error description.
   */
  private _onError = (error: { error: { code: string; message: string } }) => {
    this.isEventError = true;
    const config = this.getErrorConfiguration(error)!;
    this._modalService.openModal(config);
    return of();
  };

  /**
   * Determines the appropriate configuration for the modal based on the error message.
   *
   * @param  errorMessage - The error message received from the error object.
   * @returns An object containing the modal configuration.
   */
  private getErrorConfiguration(error: { error?: { code: string; message: string } }) {
    const formattedErrorMessage = getMessageError(error.error!);
    return {
      type: 'app',
      text: formattedErrorMessage!,
      buttonText: 'Close',
      buttonAction: () => {
        this._modalService.closeModal();
      },
    };
  }

  /**
   * Sets the events data.
   * @param events - The events data to set.
   */
  private _setEvents = (events: EventModel[]) => {
    this._events.next(events);
  };

  /**
   * Selects the configuration for the selected event.
   */
  public selectEventConfiguration = () => {
    const availableConfigurations = this._sessionService.getAvailableConfigurations();
    if (!availableConfigurations || !this.selectedEvent) {
      return;
    }

    const eventConfiguration = availableConfigurations.find(cfg => cfg.eventId === this.selectedEvent?.id);
    const primaryConfiguration = availableConfigurations.find(cfg => cfg.type === CONFIGURATION_TYPE.PRIMARY);

    if (eventConfiguration && primaryConfiguration) {
      const mergedConfig = this.mergeConfigurations(eventConfiguration, primaryConfiguration);
      this.setEventConfiguration(mergedConfig);
    } else {
      this.setEventConfiguration(primaryConfiguration!);
    }
  };

  /**
   * Gets the current event configuration.
   * @returns {ConfigurationModel | null} - The current event configuration.
   */
  public getEventConfiguration = (): ConfigurationModel | null => {
    return this._eventConfiguration.getValue();
  };

  /**
   * Sets the event configuration.
   * @param configuration - The configuration to set.
   */
  public setEventConfiguration = (configuration: ConfigurationModel) => {
    this._eventConfiguration.next(configuration);
  };

  /**
   * Resets the event configuration to null.
   */
  public resetEventConfiguration = () => {
    this._eventConfiguration.next(null);
  };

  /**
   * Merges the event-specific configuration with the primary configuration.
   * @param eventConfig - The event-specific configuration.
   * @param primaryConfig - The primary configuration.
   * @returns {ConfigurationModel} - The merged configuration.
   */
  private mergeConfigurations = (
    eventConfig: ConfigurationModel,
    primaryConfig: ConfigurationModel
  ): ConfigurationModel => {
    const mergedConfiguration = {};
    Object.assign(mergedConfiguration, primaryConfig);
    Object.assign(mergedConfiguration, eventConfig);

    return mergedConfiguration as ConfigurationModel;
  };
}

/**
 * Resolve function to initialize the events service.
 */
export const patronEventsResolver: ResolveFn<any> = () => {
  assertInInjectionContext((error: any) => console.log('Injector context error:', error));

  return inject(EventsService).initialize();
};
